import { Injectable, NgZone } from '@angular/core';

import { environment } from 'src/environments/environment';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import { Observable, Subject } from 'rxjs';
import { NbToastrService, NbGlobalPosition, NbGlobalPhysicalPosition, NbComponentStatus, NbDialogService } from '@nebular/theme';
import { Router } from '@angular/router';
import { User } from '../models/user';
import { UserForgotPasswordViewComponent } from '../pages/auth/user-forgot-password-view/user-forgot-password-view.component';
import Auth, { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { Hub, ICredentials } from '@aws-amplify/core';

const POOL_DATA = {
  UserPoolId: environment.userPoolId,
  ClientId: environment.clientId
};
const userPool = new CognitoUserPool(POOL_DATA);
const loginErrorMessage = 'Usuario y/o Contraseña incorrecta';

@Injectable()
export class AuthService {
  public static SIGN_IN = 'signIn';
  public static SIGN_OUT = 'signOut';
  public static GOOGLE = CognitoHostedUIIdentityProvider.Google;
  public loggedIn: boolean;
  private authStates: Subject<CognitoUser | any> = new Subject<CognitoUser | any>();
  authState: Observable<CognitoUser | any> = this.authStates.asObservable();

  AWS = require('aws-sdk');
  authStatusChanged = new Subject<boolean>();
  userProfileChanged = new Subject<boolean>();
  succedded = false;


  constructor(
    private toastrService: NbToastrService,
    private ngZone: NgZone,
    private router: Router,
    private dialogService: NbDialogService,
  ) {
    Hub.listen('auth', (data) => {
      const { channel, payload } = data;
      if (channel === 'auth') {
        if (payload.event === 'cognitoHostedUI') {
          this.getUser(payload.data.username).then(
            (auth: any) => {
              const userBackend = auth.UserAttributes;
              const sub = auth.Username;
              const username = userBackend.find((x) => x.Name === 'email');
              const email = userBackend.find((x) => x.Name === 'email');
              const name = userBackend.find((x) => x.Name === 'name');
              const picture = userBackend.find((x) => x.Name === 'picture');
              const userRole = userBackend.find((x) => x.Name === 'custom:role');
              const company = userBackend.find((x) => x.Name === 'custom:company');
              const theme = userBackend.find((x) => x.Name === 'custom:theme');
              let faceId = userBackend.find((x) => x.Name === 'custom:faceId');
              if (faceId === undefined) {
                faceId = {};
                faceId.Value = undefined;
              }
              this.succedded = true;
              const userLogged = {
                userId: sub,
                email: email.Value,
                username: sub.Value,
                picture: picture.Value,
                name: name.Value,
                userRole: userRole.Value,
                password: undefined,
                userStatus: undefined,
                company: {
                  companyCode: company.Value,
                  companyName: undefined,
                  companyNameForFilter: undefined,
                },
                theme: theme.Value,
                faceId: faceId.Value,
              };
              localStorage.setItem('user', JSON.stringify(userLogged));
              this.authStatusChanged.next(true);
              const position: NbGlobalPosition = NbGlobalPhysicalPosition.TOP_RIGHT;
              const status: NbComponentStatus = 'success';
              this.toastrService.show('', 'Bienvenido ' + name.Value,
                { position, status });
              this.navigate(['']);

            }).catch(
              (err) => {
                console.error(err);
              });
        }
        this.authStates.next(payload.event);
      }
    });
  }

  navigate(commands: any[]): void {
    this.ngZone.run(() => this.router.navigate(commands)).then();
  }

  getAuthenticatedUser() {
    return userPool.getCurrentUser();
  }

  getAuthenticatedUserSession() {
    let sessionReturn: any;

    userPool.getCurrentUser().getSession(
      (err, session) => {
        if (err) {
          const position: NbGlobalPosition = NbGlobalPhysicalPosition.TOP_RIGHT;
          const status: NbComponentStatus = 'danger';
          this.toastrService.show(
            status || '',
            'Error en Session Actual',
            { position, status });
        }
        sessionReturn = session;
      }
    );
    return sessionReturn;

  }

  signIn(username: string, password: string): any {
    const authData = {
      Username: username,
      Password: password
    };
    const authDetails = new AuthenticationDetails(authData);
    const userData = {
      Username: username,
      Pool: userPool
    };
    const cognitoUser = new CognitoUser(userData);
    const that = this;
    this.succedded = false;
    cognitoUser.authenticateUser(authDetails, {
      onSuccess(result: any) {
        that.succedded = true;
        that.authStatusChanged.next(true);
        const position: NbGlobalPosition = NbGlobalPhysicalPosition.TOP_RIGHT;
        const status: NbComponentStatus = 'success';
        that.toastrService.show('', 'Bienvenido ' + result.idToken.payload.name,
          { position, status });
        that.router.navigate(['']);
        const userLogged = {
          userId: result.idToken.payload.sub,
          email: result.idToken.payload.email,
          username: result.idToken.payload['cognito:username'],
          picture: result.idToken.payload.picture,
          name: result.idToken.payload.name,
          userRole: result.idToken.payload['custom:role'],
          password: undefined,
          userStatus: undefined,
          company: {
            companyCode: result.idToken.payload['custom:company'],
            companyName: undefined,
            companyNameForFilter: undefined,
          },
          theme: result.idToken.payload['custom:theme'],
          faceId: result.idToken.payload['custom:faceId'],
        };
        localStorage.setItem('user', JSON.stringify(userLogged));
      },
      onFailure(err: any) {
        that.authStatusChanged.next(false);
        const position: NbGlobalPosition = NbGlobalPhysicalPosition.TOP_RIGHT;
        const status: NbComponentStatus = 'danger';
        that.toastrService.show(
          status || '',
          loginErrorMessage,
          { position, status });
      }
    });

    this.authStatusChanged.next(this.succedded);
  }

  isAuthenticated(): Observable<boolean> {
    const user = this.getAuthenticatedUser();
    const obs = new Observable<boolean>(observer => {
      if (!user) {
        observer.next(false);
      } else {
        user.getSession((err, session) => {
          if (err) {
            observer.next(false);
          }
          if (session.isValid()) {
            observer.next(true);
          } else {
            observer.next(false);
          }
        });
      }
      observer.complete();
    });
    return obs;
  }

  async logout() {
    await this.signOut();
    // this.getAuthenticatedUser().signOut();
    this.authStatusChanged.next(false);
    localStorage.clear();
  }

  initAuth() {
    this.isAuthenticated().subscribe(auth => this.authStatusChanged.next(auth));
  }

  socialSignIn(provider: CognitoHostedUIIdentityProvider): Promise<ICredentials> {
    return Auth.federatedSignIn({
      provider
    });
  }

  signOut(): Promise<any> {
    return Auth.signOut()
      .then(() => this.loggedIn = false);
  }

  signUp(username: string, email: string, password: string, name: string, userRole: any, picture: string, group: string, userRekognitionImage: string, universityID: string) {
    const userLogged: User = JSON.parse(localStorage.getItem('user'));
    const user: User = {
      username,
      email,
      name,
      userRole,
      picture,
      password,
      userStatus: undefined,
      theme: userLogged.theme,
      company: userLogged.company,
      userId: undefined,
      userRekognitionImage: name,
      group
    };
    const attrList: CognitoUserAttribute[] = [];
    const emailAttribute = {
      Name: 'email',
      Value: user.email
    };
    const nameAttribute = {
      Name: 'name',
      Value: user.name
    };
    const roleAttribute = {
      Name: 'custom:role',
      Value: userRole
    };
    const themeAttribute = {
      Name: 'custom:theme',
      Value: 'default'
    };
    const companyAttribute = {
      Name: 'custom:company',
      Value: userLogged.company.companyCode
    };
    const pictureAttribute = {
      Name: 'picture',
      Value: user.email
    };
    const groupAttribute = {
      Name: 'custom:group',
      Value: user.group
    };
    const rekognitionImageAttribute = {
      Name: 'custom:rekognitionImage',
      Value: user.userRekognitionImage
    };
    attrList.push(new CognitoUserAttribute(emailAttribute));
    attrList.push(new CognitoUserAttribute(nameAttribute));
    attrList.push(new CognitoUserAttribute(roleAttribute));
    attrList.push(new CognitoUserAttribute(themeAttribute));
    attrList.push(new CognitoUserAttribute(companyAttribute));
    attrList.push(new CognitoUserAttribute(pictureAttribute));
    attrList.push(new CognitoUserAttribute(groupAttribute));
    attrList.push(new CognitoUserAttribute(rekognitionImageAttribute));

    return new Promise((resolve, reject) => {
      userPool.signUp(user.username, user.password, attrList, null, (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    });
  }

  getUsers() {
    const params = {
      UserPoolId: environment.userPoolId,
    };

    return new Promise((resolve, reject) => {
      this.AWS.config.update({
        region: environment.region,
        accessKeyId: environment.accessKeyId,
        secretAccessKey: environment.secretAccessKey
      });
      const cognitoidentityserviceprovider = new this.AWS.CognitoIdentityServiceProvider();
      cognitoidentityserviceprovider.listUsers(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  disableUser(username) {
    const params = {
      UserPoolId: environment.userPoolId,
      Username: username,
    };
    return new Promise((resolve, reject) => {
      this.AWS.config.update({
        region: environment.region,
        accessKeyId: environment.accessKeyId,
        secretAccessKey: environment.secretAccessKey
      });
      const cognitoidentityserviceprovider = new this.AWS.CognitoIdentityServiceProvider();
      cognitoidentityserviceprovider.adminDisableUser(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  enableUser(username) {
    const params = {
      UserPoolId: environment.userPoolId,
      Username: username,
    };
    return new Promise((resolve, reject) => {
      this.AWS.config.update({
        region: environment.region,
        accessKeyId: environment.accessKeyId,
        secretAccessKey: environment.secretAccessKey
      });
      const cognitoidentityserviceprovider = new this.AWS.CognitoIdentityServiceProvider();
      cognitoidentityserviceprovider.adminEnableUser(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  getUser(username) {
    const params = {
      UserPoolId: environment.userPoolId,
      Username: username,
    };

    return new Promise((resolve, reject) => {
      this.AWS.config.update({
        region: environment.region,
        accessKeyId: environment.accessKeyId,
        secretAccessKey: environment.secretAccessKey
      });
      const cognitoidentityserviceprovider = new this.AWS.CognitoIdentityServiceProvider();
      cognitoidentityserviceprovider.adminGetUser(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  getUsersFromApp(userPoolId) {
    const params = {
      UserPoolId: userPoolId,
      AttributesToGet: [
        'name',
        'custom:role',
        'email',
        'custom:company',
        'sub'
      ],
    };

    return new Promise((resolve, reject) => {
      this.AWS.config.update({
        region: environment.region,
        accessKeyId: environment.accessKeyId,
        secretAccessKey: environment.secretAccessKey
      });
      const cognitoidentityserviceprovider = new this.AWS.CognitoIdentityServiceProvider();
      cognitoidentityserviceprovider.listUsers(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  updateUserRekognitionImage(studentImage, student) {
    const attrList: CognitoUserAttribute[] = [];
    const rekognitionImageAttribute = {
      Name: 'custom:rekognitionImage',
      Value: studentImage
    };
    attrList.push(new CognitoUserAttribute(rekognitionImageAttribute));
    const params = {
      UserPoolId: environment.userPoolId,
      Username: student.userId,
      UserAttributes: attrList,
    };
    return new Promise((resolve, reject) => {
      this.AWS.config.update({
        region: environment.region,
        accessKeyId: environment.accessKeyId,
        secretAccessKey: environment.secretAccessKey
      });
      const cognitoidentityserviceprovider = new this.AWS.CognitoIdentityServiceProvider();
      cognitoidentityserviceprovider.adminUpdateUserAttributes(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  updateUser(user: User) {
    const attrList: CognitoUserAttribute[] = [];
    const emailAttribute = {
      Name: 'email',
      Value: user.email
    };
    const nameAttribute = {
      Name: 'name',
      Value: user.name
    };
    const themeAttribute = {
      Name: 'custom:theme',
      Value: user.theme
    };
    const pictureAttribute = {
      Name: 'picture',
      Value: user.picture
    };
    const rekognitionImageAttribute = {
      Name: 'custom:rekognitionImage',
      Value: user.userRekognitionImage
    };
    const userGroupAttribute = {
      Name: 'custom:group',
      Value: user.group
    };
    const userRoleAttribute = {
      Name: 'custom:role',
      Value: user.userRole.toString()
    };
    attrList.push(new CognitoUserAttribute(emailAttribute));
    attrList.push(new CognitoUserAttribute(nameAttribute));
    attrList.push(new CognitoUserAttribute(themeAttribute));
    attrList.push(new CognitoUserAttribute(pictureAttribute));
    attrList.push(new CognitoUserAttribute(rekognitionImageAttribute));
    attrList.push(new CognitoUserAttribute(userGroupAttribute));
    attrList.push(new CognitoUserAttribute(userRoleAttribute));
    const params = {
      UserPoolId: environment.userPoolId,
      Username: user.userId,
      UserAttributes: attrList,
    };

    return new Promise((resolve, reject) => {
      this.AWS.config.update({
        region: environment.region,
        accessKeyId: environment.accessKeyId,
        secretAccessKey: environment.secretAccessKey
      });
      const cognitoidentityserviceprovider = new this.AWS.CognitoIdentityServiceProvider();
      cognitoidentityserviceprovider.adminUpdateUserAttributes(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  confirmUser(username: string, code: string) {
    const userData = {
      Username: username,
      Pool: userPool
    };
    const cognitUser = new CognitoUser(userData);
    return new Promise((resolve, reject) => {
      cognitUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    });
  }

  forgotPassword(username: string) {
    const that = this;
    const userData = {
      Username: username,
      Pool: userPool
    };
    const cognitoUser = new CognitoUser(userData);
    cognitoUser.forgotPassword({
      onSuccess(result) {
        const position: NbGlobalPosition = NbGlobalPhysicalPosition.TOP_RIGHT;
        const status: NbComponentStatus = 'success';
        that.toastrService.show('', 'Contraseña restablecida', { position, status });
        that.router.navigateByUrl('/login');
      },
      onFailure(err) {
        const position: NbGlobalPosition = NbGlobalPhysicalPosition.TOP_RIGHT;
        const status: NbComponentStatus = 'danger';
        if (err.name == 'InvalidParameterException'){
          that.toastrService.show('Contacte con el administrador para volver a validar su usuario', 'Usuario no verificado', { position, status });
          that.router.navigateByUrl('/login');
        } else {
          that.toastrService.show('Si su usuario lo creo con google o microsoft restablezca la contraseña desde sus páginas', 'Imposible restablecer contraseña', { position, status });
          that.router.navigateByUrl('/login');
        }
        //console.error(err);
      },

      inputVerificationCode() {
        const position: NbGlobalPosition = NbGlobalPhysicalPosition.TOP_RIGHT;
        const status: NbComponentStatus = 'success';
        that.toastrService.show( '', 'Código enviado revise su correo', { position, status });
        that.dialogService.open(UserForgotPasswordViewComponent,
          {
            hasBackdrop: true,
            closeOnEsc: true,
            closeOnBackdropClick: false
          }).onClose
          .subscribe(
            (result) => {
              const reset = {
                verificationCode: result.verificationCode,
                newPassword: result.newPassword
              };
              cognitoUser.confirmPassword(reset.verificationCode, reset.newPassword, this);
            });
      }
    });
    return true;
  }

  resendCode(userEmail) {
    const userData = {
      Username: userEmail,
      Pool: userPool
    };
    const cognitUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    });
  }

}
